<template>
  <MenuItem v-slot="{ active }">
    <component
      :is="componentType"
      :href="props.href"
      @click="click"
      :target="target"
      :rel="rel"
      :class="[
        active
          ? 'bg-gray-100 text-gray-900 dark:bg-gray-500 dark:text-white'
          : 'text-gray-700 dark:text-gray-200',
        'group flex items-center px-4 py-2 text-sm',
        classesForButton,
      ]"
      :as="props.linkAs"
      :method="props.method"
    >
      <HeroiconContainer :icon="computedIcon" class="mr-3" />
      {{ computedLabel }}
    </component>
  </MenuItem>
</template>

<script setup>
import { Link } from '@inertiajs/inertia-vue3'
import { MenuItem } from '@headlessui/vue'
import {
  PencilAltIcon,
  TrashIcon,
  QrcodeIcon,
  DocumentDownloadIcon,
  CloudUploadIcon,
  EyeIcon,
  MinusCircleIcon,
  PlusCircleIcon,
  SaveIcon,
  FolderOpenIcon,
  RefreshIcon,
} from '@heroicons/vue/solid'
import { computed } from 'vue'
import HeroiconContainer from '../HeroiconContainer.vue'

const props = defineProps({
  href: {
    type: String,
    default: '#',
  },
  type: String,
  external: Boolean,
  label: String,
  icon: Function,

  linkAs: String, // For AppLayout dropdown logout button
  method: String, // For AppLayout dropdown logout button
})

const emit = defineEmits(['click'])

function click(event) {
  // Don't want page to scroll to top
  if (props.href === '#') event.preventDefault()

  // Without timeout, clicking a dropdown item that triggers a modal to open will cause the transitions to skip
  setTimeout(() => emit('click'), 1)
}

const target = computed(() => (props.external ? '_blank' : null))

const rel = computed(() => (props.external ? 'noopener noreferrer' : null))

const componentType = computed(() =>
  props.external || props.href === '#' ? 'a' : Link
)

const classesForButton = computed(() =>
  props.linkAs === 'button' || componentType.value === 'button'
    ? 'w-full text-left'
    : ''
)

const computedLabel = computed(() => {
  if (props.label) return props.label

  return {
    edit: 'Edit',
    delete: 'Delete',
    qrcode: 'QR Code',
    export: 'Export',
    upload: 'Upload',
    view: 'View',
    exclude: 'Exclude',
    include: 'Include',
    load: 'Load',
    save: 'Save',
    restore: 'Restore',
    remove: 'Remove',
  }[props.type]
})

const computedIcon = computed(() => {
  if (props.icon) return props.icon

  return {
    edit: PencilAltIcon,
    delete: TrashIcon,
    qrcode: QrcodeIcon,
    export: DocumentDownloadIcon,
    upload: CloudUploadIcon,
    view: EyeIcon,
    exclude: MinusCircleIcon,
    include: PlusCircleIcon,
    load: FolderOpenIcon,
    save: SaveIcon,
    restore: RefreshIcon,
    remove: TrashIcon,
  }[props.type]
})
</script>
